import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "../../../src/integrations/supabase/client";
import { US_STATES } from "@/lib/constants";
import { LoadingSpinner } from "@/components/ui/loading-spinner";

export const ArticleUploadForm = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    author_name: "",
    author_email: "",
    title: "",
    state: "",
    region_id: "",
  });

  const findRegionByState = async (state: string) => {
    const { data, error } = await supabase.from("regions").select("id").contains("states", [state]).single();

    if (error) {
      console.error("Error finding region:", error);
      return null;
    }
    return data?.id || null;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFile(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.state) {
      toast({
        title: "Error",
        description: "Please select your state.",
        variant: "destructive",
      });
      return;
    }

    if (!file) {
      toast({
        title: "Error",
        description: "Please select a file to upload.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    const startTime = Date.now();

    try {
      // Find region based on selected state
      const regionId = await findRegionByState(formData.state);

      if (!regionId) {
        throw new Error("Could not determine region for selected state");
      }

      // Upload file to storage
      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `articles/${fileName}`;

      const { error: uploadError } = await supabase.storage.from("general-media").upload(filePath, file);

      if (uploadError) throw uploadError;

      const submissionData = {
        author_name: formData.author_name,
        author_email: formData.author_email,
        title: formData.title,
        region_id: regionId,
        file_path: filePath,
        file_name: file.name,
        file_type: file.type,
      };

      // Insert article record
      const { error: insertError } = await supabase.from("submitted_articles").insert([submissionData]);

      if (insertError) throw insertError;

      // Send notification to regional coordinators
      await supabase.functions.invoke("send-article-notification", {
        body: submissionData,
      });

      // Ensure minimum loading time for better UX
      const elapsedTime = Date.now() - startTime;
      if (elapsedTime < 1000) {
        await new Promise((resolve) => setTimeout(resolve, 1000 - elapsedTime));
      }

      toast({
        title: "Success",
        description: "Your article has been uploaded and submitted for review!",
      });

      setFormData({
        author_name: "",
        author_email: "",
        title: "",
        state: "",
        region_id: "",
      });
      setFile(null);

      // Reset file input
      const fileInput = document.getElementById("article-file") as HTMLInputElement;
      if (fileInput) fileInput.value = "";
    } catch (error) {
      console.error("Error uploading article:", error);
      toast({
        title: "Error",
        description: "Failed to upload article. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Submit Article</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="author_name">
              Your Name <span className="text-destructive">*</span>
            </Label>
            <Input
              id="author_name"
              value={formData.author_name}
              onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="author_email">
              Email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="author_email"
              type="email"
              value={formData.author_email}
              onChange={(e) => setFormData({ ...formData, author_email: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="state">
              State <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.state}
              onValueChange={(value) => setFormData({ ...formData, state: value })}
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select your state" />
              </SelectTrigger>
              <SelectContent>
                {US_STATES.map((state) => (
                  <SelectItem key={state} value={state}>
                    {state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="title">
              Article Title <span className="text-destructive">*</span>
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />
          </div>

          <div>
            <Label htmlFor="article-file">
              Choose File <span className="text-destructive">*</span>
            </Label>
            <div className="mt-2">
              <Button type="button" variant="outline" className="relative w-full justify-start" asChild>
                <label htmlFor="article-file" className="cursor-pointer">
                  <FileText className="h-4 w-4 mr-2" />
                  {file ? file.name : "Choose file..."}
                  <Input
                    id="article-file"
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf,.doc,.docx,.txt"
                    required
                    className="sr-only"
                  />
                </label>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-1">Accepted formats: PDF, DOC, DOCX, TXT</p>
          </div>

          <Button type="submit" disabled={loading || !file} className="w-full">
            {loading ? (
              <span className="flex items-center gap-2">
                <LoadingSpinner size={16} />
                Submitting...
              </span>
            ) : (
              "Submit Article"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
